import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";
import { KioskType, useKiosk } from "../KioskContext";
import { useMember } from "../MemberContext";
import LoadingIndicator from "./LoadingIndicator";
import { collectionErrorMessage, readCollectionHistory, type CollectionFilters, type CollectionRow, type HistoryPage, type HistoryRequest } from "../utils/collectionHistory";
import common from "../style/screens/CommonV2.module.css";
import style from "../style/components/CollectionHistoryModal.module.css";

type Target = { kind: "kiosk"; kiosk: KioskType } | {
    kind: "day"; date: string; filters: CollectionFilters; summary: string; count: number; quantity: number;
};
const formatTime = (value: number) => new Date(value).toLocaleString("ko-KR", { timeZone: "Asia/Seoul" });

export default function CollectionHistoryModal({ target, close }: { target: Target; close: () => void }) {
    const [scope, setScope] = useState<"current" | "all">("current");
    const dialog = useRef<HTMLDivElement>(null);
    const closeButton = useRef<HTMLButtonElement>(null);
    const titleId = useId();
    const tabId = useId();
    const closeRef = useRef(close);
    closeRef.current = close;

    useEffect(() => {
        const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        const overflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        closeButton.current?.focus();
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") closeRef.current();
            if (event.key !== "Tab") return;
            const items = dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled):not([tabindex="-1"]), a[href], input:not(:disabled), select:not(:disabled), [tabindex="0"]');
            if (!items?.length) return;
            const first = items[0];
            const last = items[items.length - 1];
            if (!Array.from(items).includes(document.activeElement as HTMLElement)) { event.preventDefault(); first.focus(); }
            else if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
            else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        };
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("keydown", onKeyDown);
            document.body.style.overflow = overflow;
            previousFocus?.focus();
        };
    }, []);

    const request: HistoryRequest = target.kind === "kiosk"
        ? { kioskId: target.kiosk.docId, scope, ...(scope === "current" && target.kiosk.collectionCycleId ? { cycleId: target.kiosk.collectionCycleId } : {}) }
        : { date: target.date, ...target.filters };
    const requestKey = JSON.stringify(request);

    return (
        <div className={[common["container--overlay"], common["container--overlay--active"]].join(" ")} onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}>
            <div ref={dialog} role="dialog" aria-modal="true" aria-labelledby={titleId} className={[common["unit--overlay"], common["unit--overlay--active"], style.modal].join(" ")}>
                <div className={style.header}>
                    <div>
                        <h4 id={titleId}>{target.kind === "kiosk" ? "키오스크 투입 내역" : `${target.date} 회수 내역`}</h4>
                        <p>{target.kind === "kiosk" ? `${target.kiosk.name || target.kiosk.id} · ${target.kiosk.id}` : `${target.summary} · 한국시간 기준`}</p>
                    </div>
                    <button ref={closeButton} type="button" className={style.iconButton} onClick={close} aria-label="투입 내역 닫기"><X size={18} /></button>
                </div>
                {target.kind === "kiosk" ? (
                    <>
                        <div className={style.capacity}>현재 투입량 <strong>{Number(target.kiosk.currentCapacity || 0).toLocaleString()} / {Number(target.kiosk.maxCapacity || 0).toLocaleString()}개</strong></div>
                        <div className={style.tabs} role="tablist" aria-label="투입 내역 범위">
                            {(["current", "all"] as const).map((tab) => <button id={`${tabId}-${tab}`} key={tab} type="button" role="tab" tabIndex={scope === tab ? 0 : -1} aria-selected={scope === tab} aria-controls={`${tabId}-panel`} onClick={() => setScope(tab)}
                                onKeyDown={(event) => {
                                    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
                                    event.preventDefault();
                                    const next = event.key === "Home" ? "current" : event.key === "End" ? "all" : tab === "current" ? "all" : "current";
                                    setScope(next);
                                    document.getElementById(`${tabId}-${next}`)?.focus();
                                }}>{tab === "current" ? "현재 투입분" : "전체 내역"}</button>)}
                        </div>
                    </>
                ) : <div className={style.capacity}>회수 {target.count.toLocaleString()}건 <strong>총 투입 {target.quantity.toLocaleString()}개</strong></div>}
                <div id={`${tabId}-panel`} role={target.kind === "kiosk" ? "tabpanel" : undefined} aria-labelledby={target.kind === "kiosk" ? `${tabId}-${scope}` : undefined} className={common["wrapper--overlay"]}>
                    <HistoryRows key={requestKey} requestKey={requestKey} />
                </div>
                <div className={common["wrapper--overlay--footer"]}><button type="button" className={style.button} onClick={close}>닫기</button></div>
            </div>
        </div>
    );
}

function HistoryRows({ requestKey }: { requestKey: string }) {
    const { kiosks } = useKiosk();
    const { members } = useMember();
    const kioskMap = useMemo(() => new Map(kiosks.map((item) => [item.docId, item.name || item.id])), [kiosks]);
    const memberMap = useMemo(() => new Map(members.map((item) => [item.uid, item.name])), [members]);
    const [rows, setRows] = useState<CollectionRow[]>([]);
    const [page, setPage] = useState<HistoryPage | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const sequence = useRef(0);
    const busy = useRef(false);
    const request: HistoryRequest = JSON.parse(requestKey);
    const load = useCallback(async (previous: HistoryPage | null) => {
        const token = ++sequence.current;
        busy.current = true;
        setLoading(true);
        setError("");
        try {
            const query: HistoryRequest = JSON.parse(requestKey);
            const result = await readCollectionHistory({ ...query, ...(previous ? { cursor: previous.cursor,
                ...(query.scope === "current" ? { cycleId: previous.cycleId ?? null } : {}) } : {}) });
            if (token !== sequence.current) return;
            setRows((current) => previous ? [...current, ...result.rows.filter((row) => !current.some((old) => old.id === row.id && old.source === row.source))] : result.rows);
            setPage(result);
        } catch (failure) {
            if (token === sequence.current) setError(collectionErrorMessage(failure));
        } finally {
            if (token === sequence.current) { busy.current = false; setLoading(false); }
        }
    }, [requestKey]);

    useEffect(() => {
        void load(null);
        return () => { sequence.current += 1; busy.current = false; };
    }, [load]);

    return (
        <>
            {request.scope === "current" && page?.lastEmptiedAt ? <p className={style.period}>마지막 수거: {formatTime(page.lastEmptiedAt)}</p> : null}
            <p className={style.period}>{rows.length.toLocaleString()}건 표시{request.scope === "current" ? ` · 조회한 투입량 ${rows.reduce((sum, row) => sum + row.inputAmt, 0).toLocaleString()}개` : ""} · 최신순 · 50건씩 조회</p>
            <table className={style.table} aria-busy={loading}>
                <thead><tr><th scope="col">일시</th><th scope="col">키오스크</th><th scope="col">처리</th><th scope="col">수량</th><th scope="col">이용자</th></tr></thead>
                <tbody>{rows.map((row) => (
                    <tr key={`${row.source}-${row.id}`}>
                        <td data-label="일시">{formatTime(row.timestamp)}</td>
                        <td data-label="키오스크">{kioskMap.get(row.kiosk) || row.kiosk}</td>
                        <td data-label="처리"><span className={row.type === "EMPTY" ? style.emptyBadge : row.type === "DONATE" ? style.donationBadge : style.depositBadge}>{row.type === "DONATE" ? "기부" : row.type === "DEPOSIT" ? "적립" : "수거 처리"}</span></td>
                        <td data-label="수량">{row.type === "EMPTY" ? "−" : ""}{row.inputAmt.toLocaleString()}개</td>
                        <td data-label="이용자">{row.type === "EMPTY" ? "수거 담당자" : row.owner === "DONATE" || !row.owner ? "비회원" : memberMap.get(row.owner) || row.owner}</td>
                    </tr>
                ))}</tbody>
            </table>
            {loading && !page ? <div role="status" className={style.empty}><LoadingIndicator>내역을 불러오고 있습니다.</LoadingIndicator></div> : null}
            {!loading && !error && !rows.length ? <div className={style.empty}>{page?.hasMore ? "이 구간에 표시할 투입 내역이 없습니다. 더 보기를 눌러 이전 기록을 확인해주세요." : "표시할 투입 내역이 없습니다."}</div> : null}
            {error ? <div role="alert" className={style.error}>{error} <button type="button" className={style.button} onClick={() => { if (!busy.current) void load(page); }}>다시 시도</button></div> : null}
            {page?.hasMore && !error ? <div className={style.more}><button type="button" className={style.button} disabled={loading} aria-busy={loading} onClick={() => { if (!busy.current) void load(page); }}>{loading ? <LoadingIndicator>불러오는 중</LoadingIndicator> : "더 보기 (50건)"}</button></div> : null}
        </>
    );
}
